import { Fragment, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import {
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCreateDatasetMutation, useEditDatasetMutation, useGetUsersQuery } from '~/api/v2/hooks';
import { SelectPlaceholder } from '~/components';
import { useFullName } from '~/hooks';
import { useHasLockedStatus } from '~/hooks/dataset';
import { TrashIcon } from '~/icons';
import { MAX_SECONDARY_DATA_STEWARDS } from '~/validation/datasetSchema';
import RemoveUserModal from '../DatasetModal/RemoveUserModal';

const SecondaryDataStewardBlock = () => {
  const theme = useTheme();
  const datasetId = +useParams().id!;
  const [removingStewardIndex, setRemovingStewardIndex] = useState<number | null>(null);
  const { data, isFetching } = useGetUsersQuery({});
  const { control, register, setValue, watch } = useFormContext();
  const fieldValues = watch('secondary_data_owners');
  const networkIdValues = watch('secondary_data_owner_network_ids');
  const { fullName } = useFullName();
  const { hasLockedStatus } = useHasLockedStatus();
  const createDatasetMutation = useCreateDatasetMutation({ fixedCacheKey: `create-dataset` });
  const editDatasetMutation = useEditDatasetMutation({ fixedCacheKey: `edit-dataset` });
  const isSubmitting = editDatasetMutation[1].isLoading || createDatasetMutation[1].isLoading;

  const {
    fields,
    remove: removeDataSteward,
    append: appendDataSteward,
  } = useFieldArray({
    control,
    name: 'secondary_data_owners',
  });

  const { remove: removeDataStewardNetworkId, append: appendDataStewardNetworkId } = useFieldArray({
    control,
    name: 'secondary_data_owner_network_ids',
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleRemoveSecondaryDataSteward = (index: number) => {
    removeDataSteward(index);
    removeDataStewardNetworkId(index);
  };

  const handleAddSecondaryDataSteward = () => {
    appendDataSteward('');
    appendDataStewardNetworkId('');
  };

  const handleTrashClick = (index: number) => {
    if (!datasetId || !data?.find(({ id }) => id === fieldValues[index])) {
      handleRemoveSecondaryDataSteward(index);

      return;
    }

    setRemovingStewardIndex(index);
  };

  const handleConfirmRemove = () => {
    if (removingStewardIndex === null) return;
    handleRemoveSecondaryDataSteward(removingStewardIndex);
    setRemovingStewardIndex(null);
  };

  const removingUser =
    removingStewardIndex !== null ? (data?.find(({ id }) => id === fieldValues[removingStewardIndex]) ?? null) : null;

  const isAddDisabled = hasLockedStatus || isSubmitting;
  const isLastFieldEmpty = fieldValues.length && !fieldValues[fieldValues.length - 1];

  return (
    <>
      {!!datasetId && (
        <RemoveUserModal
          user={removingUser}
          open={removingStewardIndex !== null}
          onRemove={handleConfirmRemove}
          onClose={() => setRemovingStewardIndex(null)}
        />
      )}
      {fields.map((field, index) => (
        <Fragment key={field.id}>
          <Grid size={6}>
            <FormControlLabel
              label="DATA STEWARD"
              required
              control={
                <Controller
                  name={`secondary_data_owners.${index}`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={isSubmitting || isFetching || hasLockedStatus}
                      onChange={(event) => {
                        field.onChange(event);

                        const ntid = data?.find(({ id }) => id === event.target.value)?.profile?.NTID || '';
                        setValue(`secondary_data_owner_network_ids.${index}`, ntid, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      renderValue={() => {
                        if (!field?.value)
                          return <SelectPlaceholder>Select the secondary data steward</SelectPlaceholder>;

                        return fullName(field.value);
                      }}
                    >
                      {data?.map(({ id, first_name, last_name }) =>
                        id !== watch('data_owner_name') &&
                        !fieldValues?.concat(watch('data_uploaders'))?.includes(id) ? (
                          <MenuItem key={id} value={id}>
                            {first_name} {last_name}
                          </MenuItem>
                        ) : null,
                      )}
                    </Select>
                  )}
                />
              }
            />
          </Grid>
          <Grid size={5}>
            <FormControlLabel
              label="DATA STEWARD NETWORK ID"
              required
              control={
                <TextField
                  disabled
                  data-testid={`secondary_data_owner_network_id.${networkIdValues[index]}`}
                  {...register(`secondary_data_owner_network_ids.${index}`)}
                />
              }
            />
          </Grid>
          <Grid size={1} alignSelf="center" pt={3}>
            <IconButton
              size={isSmallScreen ? 'small' : 'large'}
              color="error"
              data-testid={`secondary_data_owner.${fieldValues[index]}`}
              disabled={isSubmitting || hasLockedStatus}
              onClick={() => handleTrashClick(index)}
            >
              <TrashIcon />
            </IconButton>
          </Grid>
        </Fragment>
      ))}
      {fieldValues.length < MAX_SECONDARY_DATA_STEWARDS && !isLastFieldEmpty && (
        <Grid size={12}>
          <CustomButton buttonType="borderless" disabled={isAddDisabled} onClick={handleAddSecondaryDataSteward}>
            Add additional data steward
          </CustomButton>
        </Grid>
      )}
    </>
  );
};

export default SecondaryDataStewardBlock;
